import { useState, useMemo } from "react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  CheckCircle,
  XCircle,
  Loader2,
  Receipt,
  FileText,
  FileLock2,
} from "lucide-react";
import type { PendingExpense } from "../hooks/usePendingExpenses";
import { useBulkApproveExpenses } from "../hooks/useExpenseActions";
import { ExpenseRejectDialog } from "./ExpenseRejectDialog";
import {
  formatExpenseCategory,
  formatCurrency,
  parseExpenseDate,
} from "../../utils/expense-schema";
import { cn } from "@/lib/utils";
import {
  ExpenseReceiptPreviewDialog,
  type ExpenseReceiptPreview,
} from "../../components/ExpenseReceiptPreviewDialog";
import { ExpenseOrgNote } from "./ExpenseOrgNote";

interface ExpenseReviewTableProps {
  expenses: PendingExpense[];
  organizationBusinessUnitId: string;
  canManage?: boolean;
}

export function ExpenseReviewTable({
  expenses,
  organizationBusinessUnitId,
  canManage = true,
}: ExpenseReviewTableProps) {
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<Set<string>>(
    new Set(),
  );
  const [rejectingExpense, setRejectingExpense] =
    useState<PendingExpense | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [previewReceipt, setPreviewReceipt] =
    useState<ExpenseReceiptPreview | null>(null);

  const { mutateAsync: bulkApprove, isPending: isApproving } =
    useBulkApproveExpenses();

  // Group expenses by provider
  const groupedByProvider = useMemo(() => {
    const groups = new Map<
      string,
      { provider: { id: string; name: string }; expenses: PendingExpense[] }
    >();

    for (const expense of expenses) {
      const providerName = `${expense.providerFirstName || ""} ${
        expense.providerLastName || ""
      }`.trim();

      if (!groups.has(expense.providerId)) {
        groups.set(expense.providerId, {
          provider: {
            id: expense.providerId,
            name: providerName || "Unknown Provider",
          },
          expenses: [],
        });
      }

      groups.get(expense.providerId)!.expenses.push(expense);
    }

    return Array.from(groups.values());
  }, [expenses]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedExpenseIds(new Set(expenses.map((e) => e.expenseId)));
    } else {
      setSelectedExpenseIds(new Set());
    }
  };

  const handleSelectExpense = (expenseId: string, checked: boolean) => {
    const newSelected = new Set(selectedExpenseIds);
    if (checked) {
      newSelected.add(expenseId);
    } else {
      newSelected.delete(expenseId);
    }
    setSelectedExpenseIds(newSelected);
  };

  const handleBulkApprove = async () => {
    if (selectedExpenseIds.size === 0) {
      return;
    }

    try {
      await bulkApprove({
        organizationBusinessUnitId,
        expenseIds: Array.from(selectedExpenseIds),
      });
      setSelectedExpenseIds(new Set());
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleReject = (expense: PendingExpense) => {
    setRejectingExpense(expense);
    setRejectDialogOpen(true);
  };

  const allSelected =
    expenses.length > 0 && selectedExpenseIds.size === expenses.length;
  const someSelected = selectedExpenseIds.size > 0 && !allSelected;

  const selectedTotal = useMemo(() => {
    let total = 0;
    for (const expense of expenses) {
      if (selectedExpenseIds.has(expense.expenseId)) {
        total += parseFloat(expense.amount);
      }
    }
    return total;
  }, [expenses, selectedExpenseIds]);

  if (expenses.length === 0) {
    return (
      <Alert>
        <Receipt className="h-4 w-4" />
        <AlertDescription>
          No pending expenses to review at this time.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Bulk Actions Bar */}
      {canManage && selectedExpenseIds.size > 0 && (
        <div className="sticky top-0 z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-blue-50 border border-blue-200 rounded-md shadow-sm">
          <div className="flex items-center gap-4">
            <Badge className="bg-blue-600 text-white whitespace-nowrap">
              {selectedExpenseIds.size} selected
            </Badge>
            <div className="text-sm text-blue-700 whitespace-nowrap">
              Total: {formatCurrency(selectedTotal)}
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedExpenseIds(new Set())}
              disabled={isApproving}
              className="flex-1 sm:flex-none"
            >
              Clear Selection
            </Button>
            <Button
              size="sm"
              onClick={handleBulkApprove}
              disabled={isApproving}
              className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
            >
              {isApproving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve Selected
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden sm:block border rounded-md bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              {canManage && (
                <TableHead className="w-12">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all expenses"
                    className={cn(
                      someSelected && "data-[state=checked]:bg-blue-600",
                    )}
                  />
                </TableHead>
              )}
              <TableHead>Provider</TableHead>
              <TableHead>Facility</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Receipt</TableHead>
              <TableHead>Org Note</TableHead>
              {canManage && (
                <TableHead className="text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {groupedByProvider.map((group) => (
              <>
                {/* Provider group header */}
                <TableRow
                  key={`provider-${group.provider.id}`}
                  className="bg-slate-50"
                >
                  <TableCell
                    colSpan={canManage ? 9 : 7}
                    className="font-semibold"
                  >
                    {group.provider.name}
                    <span className="ml-2 text-sm font-normal text-slate-600">
                      ({group.expenses.length} expense
                      {group.expenses.length !== 1 ? "s" : ""})
                    </span>
                  </TableCell>
                </TableRow>

                {/* Provider's expenses */}
                {group.expenses.map((expense) => (
                  <TableRow
                    key={expense.expenseId}
                    className={cn(
                      canManage &&
                        selectedExpenseIds.has(expense.expenseId) &&
                        "bg-blue-50/50",
                    )}
                  >
                    {canManage && (
                      <TableCell>
                        <Checkbox
                          checked={selectedExpenseIds.has(expense.expenseId)}
                          onCheckedChange={(checked) =>
                            handleSelectExpense(
                              expense.expenseId,
                              checked as boolean,
                            )
                          }
                          aria-label={`Select expense for ${expense.providerFirstName} ${expense.providerLastName}`}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="text-sm">
                        {expense.providerFirstName} {expense.providerLastName}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-[200px]">
                        <div className="text-sm font-medium truncate">
                          {expense.facilityName}
                        </div>
                        {expense.facilityCity && expense.facilityState && (
                          <div className="text-xs text-slate-500">
                            {expense.facilityCity}, {expense.facilityState}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {formatExpenseCategory(expense.category)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {format(
                        parseExpenseDate(expense.expenseDate),
                        "MMM d, yyyy",
                      )}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(expense.amount)}
                    </TableCell>
                    <TableCell>
                      {expense.receipts.length === 1 ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPreviewReceipt(expense.receipts[0])}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      ) : (
                        <div className="flex flex-col gap-1">
                          {expense.receipts.map((receipt, idx) => (
                            <Button
                              key={`${receipt.path}-${idx}`}
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs justify-start"
                              onClick={() => setPreviewReceipt(receipt)}
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              {idx + 1}. {receipt.fileName.substring(0, 15)}...
                            </Button>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <ExpenseOrgNote
                        expenseId={expense.expenseId}
                        organizationBusinessUnitId={organizationBusinessUnitId}
                        orgNote={expense.orgNote}
                        canEdit={canManage}
                        popover
                      />
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleSelectExpense(expense.expenseId, true)
                            }
                            disabled={
                              selectedExpenseIds.has(expense.expenseId) ||
                              isApproving
                            }
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReject(expense)}
                            disabled={isApproving}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <div className="sm:hidden space-y-4">
        {/* Select All on Mobile */}
        {canManage && (
          <div className="flex items-center gap-3 px-1">
            <Checkbox
              checked={allSelected}
              onCheckedChange={handleSelectAll}
              aria-label="Select all expenses"
            />
            <span className="text-sm text-slate-600">Select all</span>
          </div>
        )}

        {groupedByProvider.map((group) => (
          <div
            key={`mobile-provider-${group.provider.id}`}
            className="space-y-2"
          >
            {/* Provider header */}
            <div className="px-1 py-2 font-semibold text-slate-900">
              {group.provider.name}
              <span className="ml-2 text-sm font-normal text-slate-600">
                ({group.expenses.length} expense
                {group.expenses.length !== 1 ? "s" : ""})
              </span>
            </div>

            {/* Provider's expense cards */}
            {group.expenses.map((expense) => {
              const isSelected = selectedExpenseIds.has(expense.expenseId);
              return (
                <Card
                  key={expense.expenseId}
                  className={cn(
                    "bg-white",
                    canManage && isSelected && "bg-blue-50 border-blue-200",
                  )}
                >
                  <CardContent className="p-3 space-y-3">
                    {/* Header row */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {canManage && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) =>
                              handleSelectExpense(
                                expense.expenseId,
                                checked as boolean,
                              )
                            }
                            className="mt-1"
                          />
                        )}
                        <div>
                          <div className="font-medium">
                            {format(
                              parseExpenseDate(expense.expenseDate),
                              "EEE, MMM d, yyyy",
                            )}
                          </div>
                          <div className="text-sm text-slate-500">
                            {expense.facilityName}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-lg">
                          {formatCurrency(expense.amount)}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {formatExpenseCategory(expense.category)}
                        </Badge>
                      </div>
                    </div>

                    {/* Receipt section */}
                    {expense.receipts.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        {expense.receipts.map((receipt, idx) => (
                          <Button
                            key={`${receipt.path}-${idx}`}
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => setPreviewReceipt(receipt)}
                          >
                            <FileText className="h-3 w-3 mr-1" />
                            {expense.receipts.length === 1
                              ? "View Receipt"
                              : `Receipt ${idx + 1}`}
                          </Button>
                        ))}
                      </div>
                    )}

                    {/* Provider note */}
                    {expense.providerNote && (
                      <div className="text-sm text-slate-600 bg-slate-50 p-2 rounded">
                        <span className="text-xs text-slate-500">Note: </span>
                        {expense.providerNote}
                      </div>
                    )}

                    {(canManage || expense.orgNote) && (
                      <div className="border-t pt-2 text-sm">
                        <div className="mb-1 flex items-center gap-1 text-xs font-medium text-slate-500">
                          <FileLock2 className="h-3.5 w-3.5" />
                          Org Note
                        </div>
                        <ExpenseOrgNote
                          expenseId={expense.expenseId}
                          organizationBusinessUnitId={
                            organizationBusinessUnitId
                          }
                          orgNote={expense.orgNote}
                          canEdit={canManage}
                          popover
                        />
                      </div>
                    )}

                    {/* Actions */}
                    {canManage && (
                      <div className="flex items-center justify-end gap-1 pt-2 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            handleSelectExpense(expense.expenseId, true)
                          }
                          disabled={isSelected || isApproving}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50 h-8 px-2"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReject(expense)}
                          disabled={isApproving}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ))}
      </div>

      {/* Reject Dialog */}
      <ExpenseRejectDialog
        expense={rejectingExpense}
        organizationBusinessUnitId={organizationBusinessUnitId}
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
      />
      <ExpenseReceiptPreviewDialog
        receipt={previewReceipt}
        onClose={() => setPreviewReceipt(null)}
      />
    </div>
  );
}
